import React from 'react';
import { useSelector } from 'react-redux';
import Section from '@components/Section';
import { attributesForContactInfo } from '@models/Contact';
import { ContactInfo, ID } from '@app/types';
import styles from './Sources.module.scss';
import { summarySelector } from '@actions/moments';
import analytics from '@support/analytics';
import { useThemedIconForSource } from '@hooks/use-themed-icon-for-source';
import { useAuthContext } from '@hooks/use-auth-context';
import SourceActionsMenu from './ActionsMenu';
import PhoneSource from './PhoneSource';
import CopySourceBtn from './CopySourceBtn';
import { ContactSource, selectContactSources } from '@reducers/data/sources';

function SourceIcon({ source }: { source: ContactSource }) {
  const icon = useThemedIconForSource(source, { baseIconOnIntegration: true });
  return <span className={styles.icon} style={{ backgroundImage: `url(${icon})` }} />;
}

function handleLink() {
  analytics.track('Contact Contacted');
}

function Source({ source, contactID }: { source: ContactSource; contactID: ID }) {
  const { getIsFreePlan } = useAuthContext();
  const { href, target, displayValue, label } = attributesForContactInfo(source, getIsFreePlan());

  if (href) {
    if (source.type === 'email') {
      return (
        <div className={styles.source}>
          <a className={styles.value} href={href} target={target} onClick={handleLink}>
            {displayValue}
          </a>
          <div className={styles.label}>{label}</div>
          <SourceActionsMenu
            contactID={contactID}
            source={source}
            triggerClassName={styles.sourceActionsMenuTrigger}
          />
        </div>
      );
    }

    if (source.type === 'phone') {
      return (
        <div className={styles.source}>
          <PhoneSource source={source} />
          <div className={styles.label}>{label}</div>
          <SourceActionsMenu
            contactID={contactID}
            source={source}
            triggerClassName={styles.sourceActionsMenuTrigger}
          />
        </div>
      );
    }

    return (
      <a className={styles.source} href={href} target={target} onClick={handleLink}>
        <span className={styles.value}>{displayValue}</span>
        <CopySourceBtn value={href} className={styles.copyBtn} />
      </a>
    );
  }

  return (
    <div className={styles.source}>
      {source.value}
      <CopySourceBtn value={source.value} className={styles.copyBtn} />
    </div>
  );
}

export const SourcesList = ({
  contactID,
  summary,
  sources,
}: {
  contactID?: ID;
  summary?: String[];
  sources?: ContactInfo[];
}) => {
  let contactSources = useSelector(selectContactSources(contactID)).items.valueSeq().toArray();
  contactSources = sources ? (sources as ContactSource[]) : contactSources;

  if (contactSources.length === 0) {
    return null;
  }

  return (
    <Section>
      <Section.Title underline>Sources</Section.Title>
      {summary && <p>{summary.join(' ')}</p>}
      {contactSources.map((source) => (
        <div className={styles.sourceContainer} key={source.id}>
          <SourceIcon source={source} />
          <Source contactID={contactID} source={source} />
        </div>
      ))}
    </Section>
  );
};

export default function Sources({
  sources,
  contactID,
  isSelf = false,
}: {
  sources?: ContactInfo[];
  contactID?: ID;
  isSelf?: boolean;
}) {
  const summary = useSelector(summarySelector(contactID || ''));
  return <SourcesList contactID={contactID} sources={sources} summary={!isSelf && summary} />;
}
